/* Shared query handling; retain each retailer's native facets, cards and sorting. */
window.RetailerSearch = (() => {
  const query = window.AtomSearchQuery;
  const strings = window.RetailerSearchStrings;
  const searchPath = (window.Shopify?.routes?.root || '/') + 'search';

  function longRunCollection(value, type = '') {
    if (type && !/cob|led strip/i.test(type)) return null;
    const raw = String(value || '').trim().toLowerCase();
    if (/[\w.]+:|["()]/.test(raw)) return null;
    const normalized = raw.replace(/\b20[\s-]*(?:metres?|meters?|m)\b/g, '20m');
    const words = normalized.split(/\s+/);
    const allowed = new Set(['20m', 'cob', 'led', 'strip', 'strips', 'light', 'lights', 'length', 'long', 'run']);
    if (!words.includes('20m') || !words.some(word => word === 'strip' || word === 'strips') || !words.every(word => allowed.has(word))) return null;
    return new URL((window.Shopify?.routes?.root || '/') + 'collections/20m-led-strip-lights', location.origin);
  }
  function url(value, type = '') {
    const target = new URL(searchPath, location.origin);
    target.searchParams.set('q', query.compile(value));
    target.searchParams.set('type', 'product');
    target.searchParams.set('options[prefix]', 'last');
    target.searchParams.set('options[unavailable_products]', 'last');
    if (type) target.searchParams.set('filter.p.product_type', type);
    return target;
  }
  function suggestions(value, type = '') {
    const target = url(value, type);
    if (longRunCollection(value, type)) target.searchParams.set('q', 'cob led strip 20m');
    target.searchParams.set('section_id', 'retailer-search-suggestions');
    return target.href;
  }
  function format(root) {
    if (!root) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (node.parentElement?.closest('script,style,textarea')) continue;
      const display = query.display(node.nodeValue);
      if (display !== node.nodeValue) node.nodeValue = display;
    }
  }
  function rank(root, value) {
    const list = root.querySelector('.retailer-suggestions [role="listbox"]');
    if (!list || list.closest('[data-long-run-collection]')) return;
    const raw = String(value).trim().toLowerCase();
    const normalized = query.normalize(query.normalizeQuery(value));
    const wanted = query.specifications(normalized);
    const ranked = [...list.children].map(row => {
      const title = query.normalize(row.querySelector('.retailer-suggestion-title')?.textContent);
      const skus = JSON.parse(row.dataset.productSkus || '[]').map(s => String(s).toLowerCase());
      let score = title.includes(normalized) ? 200 : 100;
      if (skus.includes(raw)) score = 10000;
      else if (raw.length >= 4 && skus.some(s => s.startsWith(raw))) score = 5000;
      for (const term of normalized.split(' ').filter(t => t.length > 1)) if (title.includes(term)) score += 10;
      if (/\b(strip|strips)\b/.test(normalized) && /\bled strip\b/.test(title)) score += 30;
      if (/\bneon\b/.test(normalized) && /\bneon\b/.test(title)) score += 30;
      if (wanted.some(s => s.endsWith('leds/m')) && !/\bneon\b/.test(normalized) && /\bled strip\b/.test(title)) score += 20;
      if (!/connector|clip|profile|channel|controller|remote|accessor|end cap/.test(normalized) && /connector|mounting clip|profile|channel|controller|remote|accessor|end cap/.test(title)) score -= 50;
      return {row, score};
    }).sort((a,b) => b.score-a.score);
    list.replaceChildren(...ranked.slice(0,6).map(item => item.row));
  }
  document.addEventListener('submit', event => {
    const form = event.target;
    const input = form.querySelector('input[type="search"][name="q"]');
    if (!input || event.defaultPrevented || !input.value.trim()) return;
    if (new URL(form.action, location.href).pathname !== searchPath) return;
    event.preventDefault();
    const collection = longRunCollection(input.value);
    const target = collection || url(input.value);
    for (const [key, value] of new FormData(form)) {
      if (collection && key !== 'sort_by' && !key.startsWith('filter.')) continue;
      if (key !== 'q' && key !== 'type' && key !== 'page' && value) target.searchParams.set(key, value);
    }
    location.assign(target.href);
  });
  if (location.pathname === searchPath) {
    const current = new URL(location.href);
    const value = current.searchParams.get('q') || '';
    const compiled = query.compile(value);
    const collection = longRunCollection(value);
    if (collection) {
      if (current.searchParams.has('sort_by')) collection.searchParams.set('sort_by', current.searchParams.get('sort_by'));
      location.replace(collection.href);
    } else if (value && (value !== compiled || current.searchParams.get('type') !== 'product')) {
      current.searchParams.set('q', compiled);
      current.searchParams.set('type', 'product');
      location.replace(current.href);
    } else {
      document.querySelectorAll('input[type="search"][name="q"]').forEach(input => { input.value = query.display(value); });
      document.title = query.display(document.title);
      const main = document.querySelector('main');
      format(main);
      if (main) new MutationObserver(() => format(main)).observe(main, { childList: true, subtree: true });
    }
  }
  document.addEventListener('click', async event => {
    const link = event.target.closest('[data-retailer-load-more]');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (link.getAttribute('aria-disabled') === 'true') return;
    const controls = link.closest('[data-retailer-pagination]');
    const grid = document.querySelector('[data-retailer-search-grid]');
    const route = location.search;
    if (!grid || !controls) return;
    link.setAttribute('aria-disabled', 'true');
    controls.querySelector('[role="status"]').textContent = strings.loading;
    try {
      const next = new URL(link.href);
      if (next.origin !== location.origin) throw new Error('Invalid search URL');
      next.searchParams.set('section_id', controls.dataset.section);
      const response = await fetch(next);
      if (!response.ok) throw new Error('Search unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (!grid.isConnected || route !== location.search) return;
      const nextGrid = doc.querySelector('[data-retailer-search-grid]');
      const nextControls = doc.querySelector('[data-retailer-pagination]');
      if (!nextGrid || !nextControls) throw new Error('Missing search results');
      const ids = new Set([...grid.children].map(item => item.dataset.resultId));
      for (const item of [...nextGrid.children]) if (!ids.has(item.dataset.resultId)) { grid.append(item); ids.add(item.dataset.resultId); }
      controls.replaceWith(nextControls);
      nextControls.querySelector('[role="status"]').textContent = strings.progress.replace('__SHOWN__', grid.children.length).replace('__TOTAL__', grid.dataset.resultCount);
      nextControls.querySelector('a')?.focus({ preventScroll: true });
    } catch (error) {
      if (controls.isConnected) controls.querySelector('[role="status"]').textContent = strings.error;
    } finally {
      link.removeAttribute('aria-disabled');
    }
  });
  return { url, suggestions, format, rank };
})();
